import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getAllAnnouncements,
  getAnnouncementById,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement
} from "../Actions/AnnouncementAction";
import { Announcement, AnnouncementResponse } from "../../modules/shared/model/AnnouncementInterfaces";

interface AnnouncementState {
  announcements: Announcement[];
  selectedAnnouncement: Announcement | null;
  loading: boolean;
  error: string | null;
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
  currentLimit: number;
}

const initialState: AnnouncementState = {
  announcements: [],
  selectedAnnouncement: null,
  loading: false,
  error: null,
  totalCount: 0,
  hasNextPage: false,
  currentPage: 1,
  currentLimit: 10
};

const announcementSlice = createSlice({
  name: "announcement",
  initialState,
  reducers: {
    clearSelected: (state) => {
      state.selectedAnnouncement = null;
    },
    clearErrorState: (state) => {
      state.error = null;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setLimit: (state, action: PayloadAction<number>) => {
      state.currentLimit = action.payload;
    }
  },
  extraReducers: (builder) => {
    // Get all announcements
    builder
      .addCase(getAllAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAnnouncements.fulfilled, (state, action: PayloadAction<AnnouncementResponse>) => {
        state.loading = false;
        state.announcements = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.hasNextPage = action.payload.hasNextPage;
        state.currentPage = action.payload.currentPage;
        state.currentLimit = action.payload.currentLimit;
      })
      .addCase(getAllAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Get announcement by ID
    builder
      .addCase(getAnnouncementById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAnnouncementById.fulfilled, (state, action: PayloadAction<Announcement>) => {
        state.loading = false;
        state.selectedAnnouncement = action.payload;
      })
      .addCase(getAnnouncementById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Create announcement
    builder
      .addCase(createAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnnouncement.fulfilled, (state, action: PayloadAction<Announcement>) => {
        state.loading = false;
        state.announcements.unshift(action.payload);
        state.totalCount += 1;
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Update announcement
    builder
      .addCase(updateAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action: PayloadAction<Announcement>) => {
        state.loading = false;
        const index = state.announcements.findIndex(announcement => announcement.id === action.payload.id);
        if (index !== -1) {
          state.announcements[index] = action.payload;
        }
        if (state.selectedAnnouncement?.id === action.payload.id) {
          state.selectedAnnouncement = action.payload;
        }
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // Delete announcement
    builder
      .addCase(deleteAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.announcements = state.announcements.filter(announcement => announcement.id !== action.payload);
        state.totalCount -= 1;
        if (state.selectedAnnouncement?.id === action.payload) {
          state.selectedAnnouncement = null;
        }
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const { clearSelected, clearErrorState, setPage, setLimit } = announcementSlice.actions;
export default announcementSlice.reducer;
